/**
 * Shared zod building blocks for the entity schemas.
 *
 * Zod is the SINGLE source of truth for validation (CLAUDE.md): the same schema validates
 * on the client and in the edge function. Types are derived via z.infer (schemas/*.ts).
 *
 * KEY INVARIANT — multitenancy: `tenant_id` is NEVER accepted from client input. RLS +
 * the edge function inject it from the authenticated JWT. The *Input schemas below omit
 * tenant_id entirely; the persisted *Row schemas include it (server/read shape). Any
 * client payload that tries to set tenant_id is rejected by `.strict()`.
 */

import { z } from 'zod';

/** A UUID primary/foreign key. */
export const uuid = z.string().uuid();

/** A non-empty trimmed string (most names/labels). */
export const nonEmptyString = z.string().trim().min(1);

/** An ISO-8601 timestamp string (PostgREST returns timestamptz as ISO strings). */
export const timestamptz = z.string().datetime({ offset: true });

/** A calendar date (YYYY-MM-DD). */
export const dateString = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');

/** numeric(14,3) — a quantity. Stored as number; finite, up to 3 decimals. */
export const quantity = z.number().finite();

/** A non-negative quantity. */
export const nonNegativeQuantity = z.number().finite().nonnegative();

/** numeric(6,2) — a percentage. */
export const percent = z.number().finite();

/**
 * A tolerance band in %, bounded to [0, 100] — mirrors the
 * recipe_ingredients_tolerance_pct_range CHECK so client and server reject the
 * same values. 0 is legal and MEANS zero tolerance (hit the target exactly);
 * "no tolerance configured" is expressed as null, never as 0.
 */
export const tolerancePct = z.number().finite().min(0).max(100);

/** numeric(14,4) — money. */
export const money = z.number().finite();

/** A free-text note (nullable in DB). */
export const noteText = z.string().trim().min(1);

/** jsonb column — opaque object. Use a tighter schema where the shape is known. */
export const jsonObject = z.record(z.string(), z.unknown());

/**
 * Soft-delete columns present on every soft-deletable row (read shape). Apps never set
 * these from a generic update; deletion goes through a dedicated soft-delete flow that
 * requires `deletion_reason` (invariant: soft delete everywhere, with a reason).
 */
export const softDeleteFields = {
  deleted_at: timestamptz.nullable(),
  deleted_by: uuid.nullable(),
  deletion_reason: z.string().trim().min(1).nullable(),
};

/** Audit/timestamp columns present on the read shape of most rows. */
export const timestampFields = {
  created_at: timestamptz,
  updated_at: timestamptz,
};

/**
 * Payload for a soft delete (shared across masters). A written reason is MANDATORY
 * (CLAUDE.md): baixa lògica amb motiu, mai DELETE físic.
 */
export const SoftDeleteInput = z
  .object({
    deletion_reason: z.string().trim().min(1),
  })
  .strict();
export type SoftDeleteInput = z.infer<typeof SoftDeleteInput>;

/**
 * Offline-sync columns shared by planta-writable, offline-first tables. `client_uuid` is
 * the idempotency key generated on the device; `sync_status` reconciles on sync.
 */
export const syncFields = {
  client_uuid: uuid.nullable(),
  sync_status: z.enum(['pending', 'synced', 'conflict']),
};
